import { useEffect, useMemo } from 'react';
import { useQuery, UseQueryResult, useQueryClient } from '@tanstack/react-query';

import { useInsightsParams } from './useInsightsParams';
import { GraphqlQueryDataResponse } from '@insights_module/types/graphql/ClientTypes';
import { useMyLibraryContext } from '@insights_module/context/MyLibraryContext';
import { graphqlApi } from '@configs/axios-instances';
import { tokenToObject } from '@utils/commonUtils';
import {
  AQLBrandSafetyGarmFilterQuery,
  AQLBrandSafetyGarmQuery,
  AQLNameQuery,
} from '@insights_module/adc_ql/queries';
import { AQLQueryValue } from '@insights_module/adc_ql/types';

const groupsWithProcessedAudioCountAndGarmQuery = `query Groups($pageSize: String, $pageNo: String, $sortField: String, $sortOrder: String, $payload: String) {
    groups(page_size: $pageSize, page_no: $pageNo, sort_field: $sortField, sort_order: $sortOrder, payload: $payload) {
      data {
        id
        name
        type
        thumbnail_url
        tenant_id
        audio_count
        most_recently_published_audio_date
        external_ids {
          name
          value
        }
        extra_fields {
          name
          value
        }
        podcast_metadata {
          apple_categories
          apple_rating_count
          apple_star_rating
          author
          description
          podcast_type
        }
        created_at
        updated_at
        garm {
          adult_explicit_content
          arms_ammunition
          crime_harmful_act
          death_injury_military_conflict
          debated_social
          illegal_drugs
          hate_speech
          obscenity
          online_piracy
          spam_harmful
          terrorism
        }
        processed_audios_details {
          processed_audio_count
          most_recently_processed_audio_date
        }
        contextual {
          name
          id
          display_name
          taxonomy {
            taxonomy
            name
            tier
            id
            parent_id
          }
          audio_count
        }
      }
      pagination {
        total
        page_no
        page_size
        page_token
      }
    }
  }`;

const PAGE_SIZE = '20';

export const useLibraryGraphqlGroups = () => {
  const queryClient = useQueryClient();
  const { groupsTableSort, tableType } = useMyLibraryContext();
  const { searchTerm, queryToken } = useInsightsParams();

  const aqlQuery = useMemo(() => {
    const queryObject = tokenToObject(queryToken as string);
    const query = {
      all: [
        new AQLBrandSafetyGarmQuery([
          new AQLBrandSafetyGarmFilterQuery('adult_explicit_content', 'range', {
            gte: 'unrelated',
            lte: 'floor',
          }),
        ]),
      ] as AQLQueryValue[],
    };

    // if we have queryObject but it is not an empty array
    if (
      tableType === 'groups' &&
      queryObject &&
      typeof queryObject === 'object' &&
      !(Array.isArray(queryObject) && !queryObject.length)
    ) {
      query.all.push(queryObject);
    }

    if (searchTerm) {
      query.all.push(new AQLNameQuery('includes', searchTerm as string));
    }

    return JSON.stringify({ query });
  }, [searchTerm, queryToken, tableType]);

  const variables = useMemo(
    () => ({
      ...groupsTableSort,
      pageSize: PAGE_SIZE,
      payload: aqlQuery,
    }),
    [groupsTableSort, PAGE_SIZE, aqlQuery]
  );

  const { data, isLoading, isFetching, isPreviousData } = useQuery({
    queryKey: ['graphql', 'graphql-library-groups', variables],
    queryFn: async () =>
      graphqlApi.post('', { query: groupsWithProcessedAudioCountAndGarmQuery, variables }),
    keepPreviousData: true,
    staleTime: 60 * 60 * 1000, // 1 hour
  }) as { data: GraphqlQueryDataResponse } & UseQueryResult<unknown, unknown>;

  useEffect(() => {
    // If we have more data we will try to prefetch the next page.
    if (!data?.data?.groups?.pagination?.total) {
      return;
    }

    const hasMore =
      Number(PAGE_SIZE) * Number(variables.pageNo) < data?.data?.groups?.pagination?.total;

    const prefetchVariables = { ...variables, pageNo: String(Number(variables.pageNo) + 1) };

    if (!isPreviousData && hasMore) {
      queryClient.prefetchQuery({
        queryKey: ['graphql', 'graphql-library-groups', prefetchVariables],
        queryFn: async () =>
          graphqlApi.post('', {
            query: groupsWithProcessedAudioCountAndGarmQuery,
            variables: prefetchVariables,
          }),
      });
    }
  }, [data, isPreviousData, variables, queryClient]);

  return {
    data: data?.data?.groups?.data,
    pagination: data?.data?.groups?.pagination,
    total: data?.data?.groups?.pagination?.total,
    isLoading,
    isFetching,
    isPrefetching: isFetching,
  };
};
